import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { h2hAPI } from '../api/endpoints';
import { formatXPUNT, formatTimeAgo } from '../utils/helpers';
import { 
  FaPlus, FaSearch, FaHandshake, FaUsers, FaClock,
  FaInfoCircle, FaHistory, FaCheckCircle, FaTimesCircle,
  FaUndo, FaMoneyBillWave, FaExclamationTriangle, FaSync
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './H2H.css';

const H2H = () => {
  const { isAuthenticated, user } = useAuth();

  // Feed state
  const [loading, setLoading] = useState(true);
  const [games, setGames] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, pages: 0, current: 1 });
  const [filters, setFilters] = useState({ status: 'open', search: '' });

  // Tabs
  const [activeTab, setActiveTab] = useState('all');   // 'all' | 'mine'

  // My history
  const [history, setHistory] = useState({ summary: null, items: [] });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  // ==================== FETCH GAMES ====================
  const fetchGames = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        per_page: 20,
        page: pagination.current,
        status: filters.status || undefined,
      };
      const response = await h2hAPI.getList(params);

      const gamesData = response.data.data || [];
      const enrichedGames = gamesData.map((game) => {
        const kickoffTime = game.kickoff_time || game.start_time || game.game_time;
        const kickoffTimestamp = kickoffTime ? new Date(kickoffTime).getTime() : 0;
        const now = Date.now();
        const hasStarted = kickoffTimestamp > 0 && kickoffTimestamp <= now;
        const isEnded = game.status === 'completed' || game.status === 'refunded';
        const isLive = !isEnded && hasStarted;

        const hasValidHomeScore =
          game.home_score !== null && game.home_score !== undefined && game.home_score !== '';
        const hasValidAwayScore =
          game.away_score !== null && game.away_score !== undefined && game.away_score !== '';
        const hasScore = hasValidHomeScore && hasValidAwayScore && (isLive || isEnded);

        return {
          ...game,
          _kickoffTime: kickoffTime,
          _hasStarted: hasStarted,
          _isEnded: isEnded,
          _isLive: isLive,
          _hasScore: hasScore,
          _displayStatus: isEnded ? 'ended' : (isLive ? 'live' : 'upcoming'),
        };
      });

      setGames(enrichedGames);
      setPagination({
        total: response.data.total || 0,
        pages: response.data.pages || 0,
        current: pagination.current,
      });
    } catch (error) {
      console.error('Failed to fetch H2H games:', error);
      setGames([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.current, filters.status]);

  // ==================== FETCH MY HISTORY ====================
  const fetchHistory = useCallback(async (showLoader = false) => {
    if (!isAuthenticated) {
      setHistoryError('Please log in to see your games.');
      return;
    }
    if (showLoader) setHistoryLoading(true);
    setHistoryError(null);
    try {
      const res = await h2hAPI.getMyHistory({ limit: 50 });

      let data = res?.data;

      // Strip any accidental HTML prefix from WP DB errors
      if (typeof data === 'string') {
        const jsonStart = data.indexOf('{');
        if (jsonStart === -1) throw new Error('Invalid response from server');
        data = JSON.parse(data.slice(jsonStart));
      }

      const summary = data?.summary || data?.data?.summary || null;
      const items =
        Array.isArray(data?.items) ? data.items :
        Array.isArray(data?.data) ? data.data :
        Array.isArray(data?.data?.items) ? data.data.items :
        [];

      setHistory({ summary, items });
    } catch (err) {
      console.error('[H2H] history fetch failed:', err);
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.message;
      setHistoryError(
        status === 401 ? 'Session expired — please log in again.' :
        status === 404 ? 'History endpoint not found on the server.' :
        status === 500 ? 'Server error loading your history.' :
        msg || 'Failed to load your history.'
      );
      setHistory({ summary: null, items: [] });
    } finally {
      if (showLoader) setHistoryLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  // Fetch history when the tab switches to "mine"
  useEffect(() => {
    if (activeTab === 'mine') fetchHistory(true);
  }, [activeTab, fetchHistory]);

  // Auto-refresh history every 20s while on "mine" tab
  useEffect(() => {
    if (activeTab !== 'mine') return;
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') fetchHistory(false);
    }, 20000);
    return () => clearInterval(t);
  }, [activeTab, fetchHistory]);

  // ==================== AUTO-REFRESH LIVE MATCHES ====================
  useEffect(() => {
    const hasLiveGames = games.some((game) => game._isLive);
    if (!hasLiveGames) return;

    const interval = setInterval(() => {
      fetchGames();
    }, 30000);

    return () => clearInterval(interval);
  }, [games, fetchGames]);

  const handleFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, status: e.target.value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
  };

  const handleRefresh = async () => {
    if (activeTab === 'all') await fetchGames();
    else await fetchHistory(true);
  };

  // Filter games by search term
  const filteredGames = games.filter((game) => {
    if (!filters.search) return true;
    const search = filters.search.toLowerCase();
    return (
      game.home_team?.toLowerCase().includes(search) ||
      game.away_team?.toLowerCase().includes(search)
    );
  });

  // ==================== STATUS LABEL ====================
  const getStatusLabel = (game) => {
    if (game._isEnded) return { label: 'Ended', className: 'ended' };
    if (game._isLive) return { label: 'Live', className: 'live' };
    return { label: 'Open', className: 'open' };
  };

  // ==================== LOADING ====================
  if (loading && games.length === 0 && activeTab === 'all') {
    return (
      <div className="h2h-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="h2h-page">
      <div className="page-header">
        <div>
          <h1><FaHandshake className="header-icon" /> H2H Games</h1>
          <p className="page-subtitle">Challenge other punters head-to-head with real live matchs</p>
        </div>
        <div className="header-actions">
          {isAuthenticated && user?.punter_plus && (
            <Link to="/h2h/create" className="btn btn-primary">
              <FaPlus /> Create H2H
            </Link>
          )}
          <Link to="/virtualh2h" className="btn btn-secondary">
            <FaPlus /> Play Virtual H2H
          </Link>
        </div>
      </div>

      {/* ==================== TABS ==================== */}
      <div className="h2h-tabs">
        <button
          className={`h2h-tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
          type="button"
        >
          <FaHandshake /> All Games
        </button>
        <button
          className={`h2h-tab ${activeTab === 'mine' ? 'active' : ''}`}
          onClick={() => setActiveTab('mine')}
          type="button"
        >
          <FaHistory /> My Games
          {history.summary?.unclaimed > 0 && (
            <span className="tab-badge">
              {Number(history.summary.unclaimed).toFixed(2)} $XPUNT
            </span>
          )}
        </button>
      </div>

      {/* ==================== ALL GAMES TAB ==================== */}
      {activeTab === 'all' && (
        <>
          {/* Live Matches Notice */}
          {games.some((g) => g._isLive) && (
            <div className="live-matches-notice">
              <span className="live-dot"></span>
              <span>
                {games.filter((g) => g._isLive).length} live match(es) in progress
                — auto-refreshing every 30s
              </span>
            </div>
          )}

          {/* Filters */}
          <div className="h2h-filters">
            <div className="filter-group">
              <label>Status</label>
              <select value={filters.status} onChange={handleFilterChange}>
                <option value="open">Open</option>
                <option value="completed">Completed</option>
                <option value="refunded">Refunded</option>
                <option value="">All</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Search</label>
              <div className="search-input-wrapper">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search games..."
                  value={filters.search}
                  onChange={handleSearchChange}
                  className="search-input"
                />
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="results-info">
            <span>{filteredGames.length} games found</span>
          </div>

          {/* Games Grid */}
          {filteredGames.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🤝</div>
              <h3>No H2H games found</h3>
              <p>Be the first to create a head-to-head challenge</p>
            </div>
          ) : (
            <div className="h2h-grid">
              {filteredGames.map((game) => {
                const statusInfo = getStatusLabel(game);
                return (
                  <Link
                    to={`/h2h/${game.id}`}
                    key={game.id}
                    className={`h2h-card ${game._isLive ? 'is-live' : ''} ${game._isEnded ? 'is-ended' : ''}`}
                  >
                    {game._isLive && (
                      <div className="h2h-live-badge">
                        <span className="live-dot"></span> LIVE
                      </div>
                    )}
                    {game._isEnded && (
                      <div className="h2h-ended-badge">✅ FT</div>
                    )}

                    <div className="h2h-card-header">
                      <span className={`h2h-status ${statusInfo.className}`}>
                        {statusInfo.label}
                      </span>
                      <span className="h2h-pot">{formatXPUNT(game.total_pot)}</span>
                    </div>

                    <div className="h2h-teams">
                      <div className="h2h-team">
                        {game.home_club_logo && (
                          <img
                            src={game.home_club_logo}
                            alt={game.home_team}
                            className="h2h-team-logo"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <span className="team">{game.home_team}</span>
                      </div>

                      {game._hasScore ? (
                        <div className={`h2h-score ${game._isLive ? 'live' : 'ended'}`}>
                          <span className="score-number">{game.home_score}</span>
                          <span className="score-separator">-</span>
                          <span className="score-number">{game.away_score}</span>
                        </div>
                      ) : (
                        <span className="vs">vs</span>
                      )}

                      <div className="h2h-team">
                        {game.away_club_logo && (
                          <img
                            src={game.away_club_logo}
                            alt={game.away_team}
                            className="h2h-team-logo"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <span className="team">{game.away_team}</span>
                      </div>
                    </div>

                    {game._kickoffTime && (
                      <div className="h2h-kickoff">
                        <FaClock className="kickoff-icon" />
                        <span>
                          {game._isLive ? 'Match in progress' :
                           game._isEnded ? `Ended ${formatTimeAgo(game._kickoffTime)}` :
                           `Kicks off ${formatTimeAgo(game._kickoffTime)}`}
                        </span>
                      </div>
                    )}

                    <div className="h2h-details">
                      <div className="detail">
                        <FaUsers className="detail-icon" />
                        <span>{game.players_count || 0} players</span>
                      </div>
                      <div className="detail">
                        <FaClock className="detail-icon" />
                        <span>{formatTimeAgo(game.created_at)}</span>
                      </div>
                      <div className="detail">
                        <span className="min-stake">
                          Min: {formatXPUNT(game.minimum_stake)}
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPagination((prev) => ({ ...prev, current: prev.current - 1 }))}
                disabled={pagination.current === 1}
                className="pagination-btn"
              >
                Previous
              </button>
              <div className="pagination-pages">
                {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => {
                  let pageNum;
                  if (pagination.pages <= 5) pageNum = i + 1;
                  else if (pagination.current <= 3) pageNum = i + 1;
                  else if (pagination.current >= pagination.pages - 2) pageNum = pagination.pages - 4 + i;
                  else pageNum = pagination.current - 2 + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPagination((prev) => ({ ...prev, current: pageNum }))}
                      className={`pagination-page ${pagination.current === pageNum ? 'active' : ''}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setPagination((prev) => ({ ...prev, current: prev.current + 1 }))}
                disabled={pagination.current === pagination.pages}
                className="pagination-btn"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* ==================== MY GAMES TAB ==================== */}
      {activeTab === 'mine' && (
        <>
          {historyLoading ? (
            <div className="h2h-loading"><LoadingSpinner size="large" /></div>
          ) : historyError ? (
            <div className="h2h-feed-empty">
              <FaExclamationTriangle className="empty-icon" />
              <h3>Couldn't load your games</h3>
              <p>{historyError}</p>
              <button
                className="btn btn-primary"
                onClick={() => fetchHistory(true)}
                type="button"
              >
                <FaSync /> Try Again
              </button>
            </div>
          ) : (
            <>
              {history.summary && history.summary.total_played > 0 && (
                <div className="my-games-summary">
                  <div className="summary-stat">
                    <span className="stat-label">Played</span>
                    <span className="stat-value">{history.summary.total_played}</span>
                  </div>
                  <div className="summary-stat">
                    <span className="stat-label">Won</span>
                    <span className="stat-value success">{history.summary.won_count}</span>
                  </div>
                  <div className="summary-stat">
                    <span className="stat-label">Lost</span>
                    <span className="stat-value danger">{history.summary.lost_count}</span>
                  </div>
                  <div className="summary-stat">
                    <span className="stat-label">Total Won</span>
                    <span className="stat-value gold">
                      {Number(history.summary.total_won).toFixed(2)}
                    </span>
                  </div>
                  {history.summary.unclaimed > 0 && (
                    <div className="summary-stat highlight">
                      <span className="stat-label">
                        <FaMoneyBillWave /> Unclaimed
                      </span>
                      <span className="stat-value">
                        {Number(history.summary.unclaimed).toFixed(2)} $XPUNT
                      </span>
                    </div>
                  )}
                </div>
              )}

              {history.items.length === 0 ? (
                <div className="h2h-feed-empty">
                  <FaHistory className="empty-icon" />
                  <h3>No H2H games played yet</h3>
                  <p>Join your first head-to-head challenge to see it here.</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => setActiveTab('all')}
                    type="button"
                  >
                    Browse All Games
                  </button>
                </div>
              ) : (
                <div className="my-games-list">
                  {history.items.map((item) => (
                    <MyH2HRow key={`${item.source}-${item.stake_id}`} item={item} />
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

// ==================== MY H2H ROW ====================
const MyH2HRow = ({ item }) => {
  const statusMeta = {
    won:      { label: 'Won',      icon: <FaCheckCircle />, cls: 'won' },
    lost:     { label: 'Lost',     icon: <FaTimesCircle />, cls: 'lost' },
    refunded: { label: 'Refunded', icon: <FaUndo />,        cls: 'refunded' },
    active:   { label: 'Pending',  icon: <FaClock />,       cls: 'active' },
  };
  const meta = statusMeta[item.my_status] || statusMeta.active;

  const isCompleted = item.game_status === 'completed' || item.game_status === 'refunded';
  const showScore = isCompleted && item.home_score !== null && item.away_score !== null;

  return (
    <Link to={item.play_url} className={`my-game-row stake-${item.my_status}`}>
      <div className="my-game-left">
        <span className="source-dot h2h" />
        <div className="my-game-teams">
          <div className="mg-team">
            {item.home_club_logo && <img src={item.home_club_logo} alt="" />}
            <span>{item.home_team}</span>
          </div>
          {showScore ? (
            <div className="mg-score">
              <strong>{item.home_score}</strong>
              <span className="mg-score-sep">-</span>
              <strong>{item.away_score}</strong>
            </div>
          ) : (
            <span className="mg-vs">vs</span>
          )}
          <div className="mg-team away">
            <span>{item.away_team}</span>
            {item.away_club_logo && <img src={item.away_club_logo} alt="" />}
          </div>
        </div>
      </div>

      <div className="my-game-right">
        <div className="my-pick">
          <span className="my-pick-label">Your pick</span>
          <span className="my-pick-value">{item.my_outcome}</span>
        </div>
        <div className={`my-status ${meta.cls}`}>
          {meta.icon} {meta.label}
        </div>
        {item.my_status === 'won' && (
          <div className={`my-winnings ${item.my_claimed ? 'claimed' : 'unclaimed'}`}>
            +{Number(item.my_winnings).toFixed(2)} $XPUNT
            {!item.my_claimed && <span className="unclaimed-chip">Unclaimed</span>}
          </div>
        )}
        {item.my_status === 'refunded' && item.my_winnings > 0 && (
          <div className="my-winnings refunded">
            Refunded {Number(item.my_winnings).toFixed(2)} $XPUNT
          </div>
        )}
        {item.my_status === 'lost' && (
          <div className="my-winnings lost">
            -{Number(item.my_stake).toFixed(2)} $XPUNT
          </div>
        )}
      </div>
    </Link>
  );
};

export default H2H;